import type React from "react";
import type {GridColDef} from "@mui/x-data-grid-premium";
import {LayersOutlined, ListAltOutlined, StorageOutlined} from "@mui/icons-material";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid";
import {Tag} from "~/components/tag/Tag";
import type {SitKpiPill} from "~/sit/components/SitKpiHeader/SitKpiHeader.tsx";
import {usePtV2Context} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Context.ts";
import type {PtV2CatalogRow} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Classes.ts";
import {formatDate, formatInt, formatRatio} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Format.ts";
import {
    usePtV2CatalogQuery,
    usePtV2ProfileQuery,
    usePtV2SourcesQuery,
} from "~/experiments/pages/payment-terms-v2/usePaymentTermsV2Queries.ts";
import {PtV2TabFrame} from "~/experiments/pages/payment-terms-v2/components/PtV2TabFrame.tsx";
import {PtV2Card} from "~/experiments/pages/payment-terms-v2/components/PtV2Card.tsx";
import {PtV2Bars} from "~/experiments/pages/payment-terms-v2/components/PtV2Bars.tsx";
import {PtV2Citation} from "~/experiments/pages/payment-terms-v2/components/PtV2Citation.tsx";
import {PtV2DataManagement} from "~/experiments/pages/payment-terms-v2/components/PtV2DataManagement.tsx";
import {PtV2EmptyState} from "~/experiments/pages/payment-terms-v2/components/PtV2EmptyState.tsx";

const KIND_COLOR = {net: 'default', baseline: 'default', discount: 'success', installment: 'warning'} as const;

const CATALOG_COLUMNS: GridColDef<PtV2CatalogRow>[] = [
    {field: 'code', headerName: 'Code', width: 100, renderCell: p => <b className="ptv2-mono">{p.row.code}</b>},
    {field: 'kind', headerName: 'Type', width: 130,
        renderCell: p => <Tag color={KIND_COLOR[p.row.kind] ?? 'default'} label={p.row.kind[0].toUpperCase() + p.row.kind.slice(1)}/>},
    {field: 'description', headerName: 'Rule', flex: 1, minWidth: 300},
    {field: 'netDays', headerName: 'Net days', width: 100, type: 'number',
        renderCell: p => <span className="ptv2-num">{formatInt(p.row.netDays)}</span>},
];

export const SourcesTab: React.FC = () => {
    const {hasData, isStaff} = usePtV2Context();
    const sourcesQ = usePtV2SourcesQuery(hasData);
    const catalogQ = usePtV2CatalogQuery(hasData);
    const profileQ = usePtV2ProfileQuery(hasData);
    const layers = sourcesQ.data?.dataLayers ?? [];
    const workpapers = sourcesQ.data?.workpapers ?? [];
    const catalog = catalogQ.data ?? [];
    const cov = profileQ.data?.pdfCoverage;

    const kpis: SitKpiPill[] = [
        {tone: 'info', icon: <LayersOutlined/>, label: 'Data layers', value: formatInt(layers.length)},
        {tone: 'plain', icon: <ListAltOutlined/>, label: 'SAP term codes', value: formatInt(catalog.length)},
        {tone: 'plain', icon: <StorageOutlined/>, label: 'Records', value: formatInt(layers.reduce((s, l) => s + l.recordCount, 0))},
    ];

    // Nothing about syncing or importing is shown to non-staff users.
    const dataManagement = isStaff && <PtV2DataManagement datasets={sourcesQ.data?.datasets ?? []} lastSync={sourcesQ.data?.lastSync ?? null}/>;

    return <PtV2TabFrame
        tab="sources"
        kpis={kpis}
        loading={sourcesQ.isPending || catalogQ.isPending || profileQ.isPending}
        error={sourcesQ.isError || catalogQ.isError || profileQ.isError}
        beforeImport={<div className="ptv2-section">{dataManagement || <PtV2EmptyState/>}</div>}
        summary="Where each payment-terms figure on this page comes from: the analysis workpapers and the staged tables behind them.">
        <div className="ptv2-section">
            {dataManagement}
            <div className="ptv2-grid2">
                {layers.map(l => <PtV2Card key={l.key} title={l.label}>
                    <dl className="ptv2-kv">
                        <dt>Staged table</dt><dd><PtV2Citation>{l.table}</PtV2Citation></dd>
                        {l.sourceFile && <><dt>Source file</dt><dd>{l.sourceFile}</dd></>}
                        <dt>Runs</dt><dd>{l.runs.join('; ') || '—'}</dd>
                        <dt>Volume</dt><dd className="ptv2-num ptv2-strong">{formatInt(l.recordCount)} records</dd>
                        <dt>Key fields</dt><dd className="ptv2-mono">{l.keyFields.join(' · ')}</dd>
                    </dl>
                </PtV2Card>)}
            </div>
            {workpapers.length > 0 && <PtV2Card title="Workpapers">
                <div className="ptv2-rows">
                    {workpapers.map(w => <div className="ptv2-row" key={w.slug}>
                        <div className="ptv2-row-main">
                            <b className="ptv2-mono">{w.slug} v{w.version}</b>
                            {w.confirmedAt && <span className="ptv2-muted"> · confirmed {formatDate(w.confirmedAt)}</span>}
                            <div className="ptv2-chips">{w.sourceTables.map(t => <PtV2Citation key={t}>{t}</PtV2Citation>)}</div>
                        </div>
                        <span className="ptv2-muted ptv2-mono">{w.file}</span>
                    </div>)}
                </div>
            </PtV2Card>}
            {cov && <PtV2Card title="PDF header coverage">
                <PtV2Bars max={cov.total} rows={[
                    {key: 'terms', label: 'Payment terms text', value: cov.paymentTerms},
                    {key: 'due', label: 'Parsed due date', value: cov.dueDate},
                    {key: 'discount', label: 'Discount amount', value: cov.discountAmount},
                ].map(r => ({...r, display: <>{formatInt(r.value)}<span>{formatRatio(cov.total ? r.value / cov.total : null)}</span></>}))}/>
                <p className="ptv2-foot">Share of the {formatInt(cov.total)} parsed invoice headers where the field was extracted.</p>
            </PtV2Card>}
            <PtV2Card title="SAP payment term catalog">
                <CustomDataGrid
                    rows={catalog}
                    columns={CATALOG_COLUMNS}
                    getRowId={r => r.code}
                    autoHeight
                    hideFooter
                    disableRowSelectionOnClick
                    localeText={{noRowsLabel: 'No term codes in this export'}}
                />
            </PtV2Card>
        </div>
    </PtV2TabFrame>;
};
